import { useEffect } from 'react';
import { useAuthContext } from '../contexts/AuthContext';

/**
 * useBillingReturn — detects a return from Stripe Checkout or Billing Portal
 * and forces a Keycloak token refresh so the new plan caps take effect without
 * requiring the user to log out and back in.
 *
 * Stripe redirects to the `returnUrl` passed during session creation. We
 * append `?billing_return=1` to that URL. On mount, this hook checks for that
 * query param, fires the token refresh, then removes the param from the URL so
 * a subsequent page navigation or refresh doesn't re-trigger the refresh.
 */
export function useBillingReturn(): void {
  const { refreshToken } = useAuthContext();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('billing_return') !== '1') return;

    // Remove the param from the URL without triggering a navigation.
    params.delete('billing_return');
    const newSearch = params.toString();
    const newUrl =
      window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
    window.history.replaceState(null, '', newUrl);

    // Force-refresh the token so new plan limits/features are picked up.
    refreshToken().catch((err: unknown) => {
      console.error('[useBillingReturn] Token refresh after billing return failed:', err);
    });
  }, [refreshToken]);
}
